import React, { useCallback } from 'react'
import styled from 'styled-components'
import ModalBase from 'components/Luxkit/Modal/ModalBase'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import { rem } from 'polished'
import HeadingTextBlock from 'components/Luxkit/TextBlocks/HeadingTextBlock'
import BodyText from 'components/Luxkit/Typography/BodyText'
import TextButton from 'components/Luxkit/Button/TextButton'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import PromoCodeSection from './PromoCodeSection'
import Divider from 'components/Luxkit/Divider'
import FormatCurrency from 'components/Common/FormatCurrency'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'
import { PageType, SecondChanceModalVariant } from 'hooks/Modal/useSecondChanceModal'
import SecondChanceModalOfferCarousel from './SecondChanceModalOfferCarousel'
import SecondChanceModalSearchCarousel from './SecondChanceModalSearchCarousel'

interface Props {
  seedOffer?: App.AnyOffer
  pageType: PageType
}

const ImageContainer = styled(Group)`
  height: ${rem(168)};
  position: relative;

  ${mediaQueryUp.tablet} {
    height: ${rem(280)};
  }
`

const OverlayText = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  padding-left: ${rem(20)};
  padding-right: ${rem(20)};
  padding-top: ${rem(8)};
  padding-bottom: ${rem(8)};

  ${mediaQueryUp.tablet} {
    padding-left: ${rem(32)};
    padding-right: ${rem(32)};
    padding-top: ${rem(48)};
    padding-bottom: ${rem(48)};
  }
`

const PROMO_CODE = '2NDCHANCE20' // promo code object
const IMAGE_ID_VAR_WITHOUT_PROMO = 'vkaimkse77d8zljjsr7d'
const IMAGE_ID_VAR_WITH_PROMO = 'yx6gktjnh93xoxixc74'

type VariantConfig = {
  imageText: React.ReactElement | null;
  title: React.ReactElement | null;
  bodyText: React.ReactElement | null;
  imageId: string;
};

function getVariantConfig({ variant }: { variant: SecondChanceModalVariant }): VariantConfig {
  switch (variant) {
    case 'with_promo':
      return {
        imageText: <>Wait! Don’t miss <FormatCurrency value={20}/> Off thousands of exclusive offers</>,
        title: <>Save <FormatCurrency value={20}/> on your next booking</>,
        bodyText: <>Apply this promo code at checkout for any offer.</>,
        imageId: IMAGE_ID_VAR_WITH_PROMO,
      }
    case 'without_promo':
      return {
        imageText: <>Wait! <i>Don't miss out</i> on thousands of exclusive offers!</>,
        title: <>Before you go</>,
        bodyText: <>Take a moment to explore these top-rated escapes loved by thousands of happy customers. Discover amazing deals you won’t want to miss.</>,
        imageId: IMAGE_ID_VAR_WITHOUT_PROMO,
      }
    case 'control':
    case undefined:
      return {
        imageText: null,
        title: null,
        bodyText: null,
        imageId: '',
      }
  }
}

function SecondChanceModal({ seedOffer, pageType }: Props) {
  // The Props to showModal won't trigger the rerender, so need to get the variant from the context.
  // This page only be rendered when the variant is not control, so it would be safe without passing the second para.
  const variant = useOptimizelyExperiment(
    pageType === PageType.Offer ? OptimizelyExperiments.offerPageSecondChanceModal : OptimizelyExperiments.searchPageSecondChanceModal)

  const isWithPromo = variant === 'with_promo'
  // Modal Behaviour
  const { resolve } = useModalElementContext<'stay' | 'goBack' | undefined>()
  const onPassClick = useCallback(() => {
    resolve('goBack')
  }, [resolve])

  return (
    <ModalBase >
      <ModalBody>
        <ImageContainer direction="vertical">
          <ResponsiveImage fit="center" id={getVariantConfig({ variant }).imageId} brightness={-20}/>
          <OverlayText>
            <HeadingTextBlock
              variant="heading2"
              colour="neutral-eight"
              align="center"
            >
              {getVariantConfig({ variant }).imageText}
            </HeadingTextBlock>
          </OverlayText>
        </ImageContainer>
        <ModalContent>
          <Group direction="vertical" gap={24}>
            <Group
              direction="vertical"
              tabletDirection="horizontal"
              gap={16}
              horizontalAlign="space-between"
            >
              <Group
                direction="vertical"
                gap={4}
              >
                <HeadingTextBlock variant="heading4">{getVariantConfig({ variant }).title}</HeadingTextBlock>
                <BodyText variant="medium" colour="neutral-two">
                  {getVariantConfig({ variant }).bodyText}
                </BodyText>
              </Group>
              {isWithPromo &&
                <PromoCodeSection promoCode={PROMO_CODE} />
              }
            </Group>
            {isWithPromo && <Divider direction="horizontal" kind="primary" />}
            <Group direction="vertical" gap={16}>
              {pageType === PageType.Offer && seedOffer &&
                <SecondChanceModalOfferCarousel seedOffer={seedOffer} isWithPromo={isWithPromo} />
              }
              {pageType === PageType.Search &&
                <SecondChanceModalSearchCarousel isWithPromo={isWithPromo} />
              }
            </Group>
          </Group>
        </ModalContent>
      </ModalBody>
      <ModalFooter>
        <TextButton
          className="fit-full-width"
          kind="secondary"
          onClick={onPassClick}
        >No, I'll pass
        </TextButton>
      </ModalFooter>
    </ModalBase>
  )
}

export default SecondChanceModal
